import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, EASE } from "@/components/motion";

const STAGES = [
  { name: "Operations", line: "Property-level hotel experience" },
  { name: "Performance", line: "Revenue · Cost · Labour · KPIs" },
  { name: "Strategy", line: "Pricing · Positioning · Decisions" },
  { name: "Investment", line: "Market · Feasibility · Economics" },
  { name: "Advisory", line: "Evidence → Insight → Recommendation" },
];

const StageRow = ({ s, i }) => {
  const [active, setActive] = useState(false);
  return (
    <motion.div
      onViewportEnter={() => setActive(true)}
      viewport={{ once: true, margin: "-25% 0px" }}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: i * 0.05, ease: EASE }}
      className="relative flex items-center gap-8 border-b border-hairline py-8 pl-12 sm:py-9"
      data-testid={`journey-${s.name.toLowerCase()}`}
    >
      <span
        className={`absolute left-0 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center border text-[10px] font-semibold tracking-widest transition-colors duration-700 ${
          active ? "border-moss bg-moss text-champagne" : "border-champagne bg-stone2 text-moss"
        }`}
        aria-hidden="true"
      >
        {i + 1}
      </span>
      <div className="flex flex-1 flex-col gap-1 sm:flex-row sm:items-baseline sm:gap-6">
        <h3 className={`font-serif text-3xl sm:text-4xl font-light transition-colors duration-700 ${active ? "text-moss" : "text-ink/40"}`}>
          {s.name}
        </h3>
        <p className={`text-[12px] font-semibold uppercase tracking-[0.18em] transition-colors duration-700 ${active ? "text-smoke" : "text-fog/50"}`}>
          {s.line}
        </p>
      </div>
      <span className="hidden font-serif text-lg italic text-champagne sm:block" aria-hidden="true">0{i + 1}</span>
    </motion.div>
  );
};

const MARQUEE_ITEMS = ["Operations", "Performance", "Strategy", "Investment", "Advisory"];

export default function Journey() {
  const lineRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: lineRef, offset: ["start 80%", "end 60%"] });
  const scaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section className="border-t border-hairline bg-stone2/50" data-testid="journey-section">
      <div className="hidden overflow-hidden border-b border-hairline py-6 opacity-80 sm:block" aria-hidden="true">
        <div className="animate-marquee flex w-max items-center gap-10 whitespace-nowrap">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex items-center gap-10">
              {MARQUEE_ITEMS.concat(MARQUEE_ITEMS).map((item, i) => (
                <span key={`${dup}-${i}`} className="flex items-center gap-10">
                  <span className="font-serif text-3xl sm:text-4xl font-light italic text-ink/70">{item}</span>
                  <span className="text-champagne text-xl">→</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-6 py-28 sm:px-10 sm:py-36">
        <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <Reveal>
                <p className="overline-label">The transition</p>
              </Reveal>
              <Reveal delay={0.08}>
                <h2 className="mt-5 font-serif text-4xl sm:text-5xl font-light leading-[1.05] tracking-tight text-ink">
                  I started by operating hotels. Now I want to understand what makes them{" "}
                  <em className="text-moss">perform.</em>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="relative lg:col-span-7" ref={lineRef}>
            <motion.div
              className="absolute bottom-6 left-[13px] top-6 w-px origin-top bg-champagne"
              style={{ scaleY }}
              aria-hidden="true"
            />
            <div className="flex flex-col">
              {STAGES.map((s, i) => (
                <StageRow key={s.name} s={s} i={i} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
